/**
 * Boxed Results Stats Animation
 * Count-up animation for stat values with IntersectionObserver trigger.
 * Respects prefers-reduced-motion, accessible for screen readers.
 */
(function () {
  "use strict";

  const DATA_ATTR = "data-brc-stats-animate";
  const INITIALIZED_ATTR = "data-brc-stats-initialized";
  const ANIMATED_ATTR = "data-brc-stats-animated";

  /**
   * Parse a stat value string into numeric, prefix, and suffix components.
   * Examples: "82%" -> { num: 82, prefix: "", suffix: "%" }
   *           "195%" -> { num: 195, prefix: "", suffix: "%" }
   *           "24x" -> { num: 24, prefix: "", suffix: "x" }
   *           "$100" -> { num: 100, prefix: "$", suffix: "" }
   * @param {string} value
   * @returns {{num: number|null, prefix: string, suffix: string}}
   */
  function parseStatValue(value) {
    if (!value || typeof value !== "string") {
      return { num: null, prefix: "", suffix: "" };
    }

    const trimmed = value.trim();

    // Match optional prefix (non-digits), then numeric part, then optional suffix
    // Handles: "82%", "195%", "24x", "$100", "+50", "-10"
    const match = trimmed.match(/^([^\d\-]*)?(-?\d+(?:\.\d+)?)?([^\d]*)?$/);

    if (!match) {
      return { num: null, prefix: "", suffix: "" };
    }

    const prefix = match[1] || "";
    const numStr = match[2];
    const suffix = match[3] || "";

    const num = numStr ? parseFloat(numStr) : null;

    return { num, prefix, suffix };
  }

  /**
   * Ease out cubic function for smooth animation
   * @param {number} t - progress from 0 to 1
   * @returns {number}
   */
  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  /**
   * Animate a single stat element
   * @param {HTMLElement} el - the stat value element
   * @param {number} duration - animation duration in ms
   * @param {number} delay - delay before animation starts in ms
   */
  function animateStat(el, duration, delay) {
    const finalValue = el.textContent || "";
    const parsed = parseStatValue(finalValue);
    const circleFilled = el.closest(".is-circle")
      ? /** @type {SVGCircleElement|null} */ (
          el.closest(".is-circle").querySelector(".boxed-htu__circle-fill")
        )
      : null;
    let circleCircumference = 0;
    if (circleFilled) {
      const radius = circleFilled.r.baseVal.value;
      circleCircumference = 2 * Math.PI * radius;
      circleFilled.style.strokeDasharray = `${circleCircumference} ${circleCircumference}`;
      circleFilled.style.strokeDashoffset = String(circleCircumference);
    }

    const hasNumericValue = parsed.num !== null && !isNaN(parsed.num);
    const startNum = 0;
    const endNum = hasNumericValue ? /** @type {number} */ (parsed.num) : 0;
    const isFloat = hasNumericValue && finalValue.includes(".");

    // Set up accessibility: visually hidden final value for screen readers
    // while animating the visible value
    const visuallyHidden = document.createElement("span");
    visuallyHidden.className = "visually-hidden";
    visuallyHidden.textContent = finalValue;
    visuallyHidden.setAttribute("aria-live", "off");
    el.appendChild(visuallyHidden);

    // Mark as animating
    el.setAttribute("aria-hidden", "true");

    const startTime = performance.now() + delay;

    function step(/** @type {number} */ currentTime) {
      if (currentTime < startTime) {
        requestAnimationFrame(step);
        return;
      }

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);

      const currentNum = startNum + (endNum - startNum) * easedProgress;

      if (circleFilled) {
        // Animate circle based on percentage if available, otherwise default to 100%
        const percentage =
          parsed.suffix === "%"
            ? Math.min(Math.max(currentNum, 0), 100)
            : Math.min(Math.max(easedProgress * 100, 0), 100);
        const offset =
          circleCircumference - (percentage / 100) * circleCircumference;
        circleFilled.style.strokeDashoffset = String(offset);
      }

      // Update visible text
      let visibleText;
      if (hasNumericValue) {
        // Format number (preserve decimal places if original had them)
        let displayNum;
        if (isFloat) {
          // Determine decimal places from original
          const decimalMatch = finalValue.match(/\.(\d+)/);
          const decimalPlaces =
            decimalMatch && decimalMatch[1] ? decimalMatch[1].length : 1;
          displayNum = currentNum.toFixed(decimalPlaces);
        } else {
          displayNum = Math.round(currentNum);
        }
        visibleText = parsed.prefix + displayNum + parsed.suffix;
      } else {
        // No numeric value - keep original text
        visibleText = finalValue;
      }
      // Remove any existing text nodes before the visually-hidden span
      const textNodes = Array.from(el.childNodes).filter(
        (n) => n.nodeType === Node.TEXT_NODE
      );
      textNodes.forEach((n) => n.remove());
      // Insert new text before the visually-hidden span
      el.insertBefore(document.createTextNode(visibleText), visuallyHidden);

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        // Animation complete
        el.removeAttribute("aria-hidden");
        el.setAttribute(ANIMATED_ATTR, "true");
        // Remove visually hidden element after a brief delay
        setTimeout(() => {
          if (visuallyHidden.parentNode) {
            visuallyHidden.remove();
          }
        }, 100);
      }
    }

    requestAnimationFrame(step);
  }

  /**
   * Initialize stats animation for a section
   * @param {HTMLElement} section
   */
  function initStatsAnimation(section) {
    if (section.getAttribute(INITIALIZED_ATTR)) return;
    section.setAttribute(INITIALIZED_ATTR, "true");

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      // Skip animation, just mark as animated
      const statValues = section.querySelectorAll(
        ".brc__stat-value, .boxed-htu__stat-value"
      );
      statValues.forEach((el) => el.setAttribute(ANIMATED_ATTR, "true"));
      return;
    }

    // Get animation settings from data attributes
    const duration = parseInt(section.dataset.animationDuration || "1200", 10);
    const stagger = parseInt(section.dataset.animationStagger || "80", 10);

    const statItems = section.querySelectorAll(
      ".brc__stat-item, .boxed-htu__stat-item"
    );

    statItems.forEach((item, index) => {
      const el = /** @type {HTMLElement} */ (item);
      const valueEl = el.querySelector(
        ".brc__stat-value, .boxed-htu__stat-value"
      );
      if (!valueEl) return;

      // Add entrance animation class
      if (el.classList.contains("brc__stat-item")) {
        el.classList.add("brc__stat-item--animate-in");
      } else {
        el.classList.add("boxed-htu__stat-item--animate-in");
      }

      // Stagger the entrance
      el.style.transitionDelay = `${index * stagger}ms`;

      // Trigger count-up animation
      animateStat(
        /** @type {HTMLElement} */ (valueEl),
        duration,
        index * stagger
      );
    });

    // Trigger entrance animations after a micro-delay
    requestAnimationFrame(() => {
      statItems.forEach((item) => {
        if (item.classList.contains("brc__stat-item")) {
          item.classList.add("brc__stat-item--visible");
        } else {
          item.classList.add("boxed-htu__stat-item--visible");
        }
      });
    });
  }

  /**
   * Set up IntersectionObserver for the section
   * @param {HTMLElement} section
   */
  function observeSection(section) {
    if (section.getAttribute(INITIALIZED_ATTR)) return;

    const observerOptions = {
      root: null,
      rootMargin: "0px 0px -10% 0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          initStatsAnimation(section);
          observer.unobserve(section);
        }
      });
    }, observerOptions);

    observer.observe(section);
  }

  /**
   * Initialize all stats sections on the page
   */
  function init() {
    const sections = document.querySelectorAll(`[${DATA_ATTR}]`);
    sections.forEach(observeSection);
  }

  // Run on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-init on Shopify section events (Theme Editor)
  document.addEventListener("shopify:section:load", init);
})();
